import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import "dayjs/locale/ja";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { actions } from "app/configSlice";
import { Asset, DialogType } from "app/types";
import { AppDispatch, RootState } from "app/store";
import { useDialog, useFetchUserData } from "app/hook";
import { deleteAsset, useUserConfig, useUpdateUserConfig } from "app/api";
import { btnDelay } from "common/utils";
import { useAssetCache } from "common/hook";
import { UserAvatar, Loading } from "common/elements";
import i18n from "features/shared/i18n";
import { playerActions } from "features/player/slice";
import { UserType, AccountType } from "features/user/types";
import { actions as userActions } from "features/user/slice";
import { useAccountLimits, fetchAccountPlan } from "features/subscription";
import { messageActions, selectAllParentChatRooms } from "features/message/slice";
import {
  ProjectEntity,
  ProjectType,
  FeedbackHistoryItem,
  AIChatSession,
} from "features/player/types";
import {
  ParentConfirmModal,
  ParentWarningModal,
  AssetsSection,
  AssetTabType,
  AssetListModal,
  AssetPreviewModal,
  AppearanceEditModal,
} from "features/shared";
import {
  getUserAssets,
  deleteChild,
  updateUser,
  updateChildStatus,
  getFeedbackHistory,
  getUserActivities,
  getUserProjects,
  getUserStatus,
  getChildAIChatSessions,
} from "features/player/api";
import {
  CreatePlayerModal,
  AIChatListModal,
  PlayerIconEditor,
  GenderEditModal,
  BirthdayEditModal,
  ProjectListModal,
  ActivityListModal,
  AIChatDetailModal,
  FeedbackListModal,
  LanguageEditModal,
  ChildNameEditModal,
  FeedbackDetailModal,
  ProjectPreviewModal,
  AIChatHistorySection,
  FeedbackHistorySection,
  ProfileFieldEditModal,
  ProfileFieldType,
  ChildUserLimitModal,
  ParentProfileTab,
  PARENT_PROFILE_TAB_OPTIONS,
  ChildSelector,
  StatsCards,
  ProjectsSection,
  DangerZone,
  RecentActivitySection,
  BasicInfoSection,
  FavoritesSection,
  AppearanceSection,
  SettingsSection,
} from "./components";

interface LayoutContext {
  scrollToTop: () => void;
}

export const PlayerPage = () => {
  const { scrollToTop } = useOutletContext<LayoutContext>();
  const dispatch = useDispatch<AppDispatch>();
  const users = useSelector((state: RootState) => state.user.users);
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);
  const selectedChildUid = useSelector(
    (state: RootState) => state.player.selectedChildUid,
  );
  const chatRooms = useSelector(selectAllParentChatRooms);
  const accountLimits = useAccountLimits();
  const handleDialog = useDialog();
  const { onAssetDeleted } = useAssetCache();

  const [change, setChange] = useState(false);
  const [changeMaxWarning, setChangeMaxWarning] = useState(false);
  const [changeMinWarning, setChangeMinWarning] = useState(false);
  const [changeActiveWarning, setChangeActiveWarning] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [genderModalOpen, setGenderModalOpen] = useState(false);
  const [birthdayModalOpen, setBirthdayModalOpen] = useState(false);
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [addChildLimitWarning, setAddChildLimitWarning] = useState(false);
  const [deleteChildConfirm, setDeleteChildConfirm] = useState(false);
  const [deleteChildMinWarning, setDeleteChildMinWarning] = useState(false);
  const [profileTab, setProfileTab] = useState(ParentProfileTab.BASIC);
  const [editingField, setEditingField] = useState<ProfileFieldType | null>(
    null,
  );
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [selectedAssetTabs, setSelectedAssetTabs] = useState<Set<AssetTabType>>(
    new Set([
      AssetTabType.ALBUM,
      AssetTabType.AI_IMAGE,
      AssetTabType.SKETCHBOOK,
    ]),
  );
  const [selectedProject, setSelectedProject] = useState<ProjectEntity | null>(
    null,
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [projectListOpen, setProjectListOpen] = useState(false);
  const [assetListOpen, setAssetListOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] =
    useState<FeedbackHistoryItem | null>(null);
  const [feedbackListOpen, setFeedbackListOpen] = useState(false);
  const [selectedAIChatSession, setSelectedAIChatSession] =
    useState<AIChatSession | null>(null);
  const [aiChatListOpen, setAIChatListOpen] = useState(false);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [showFrozenWarning, setShowFrozenWarning] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const { handleFetchUserData } = useFetchUserData();

  const childUsers = users.filter((u) => u.account_type !== "parents");

  const selectedUser = useMemo(() => {
    if (selectedChildUid) {
      const found = childUsers.find((u) => u.uid === selectedChildUid);
      if (found) return found;
    }
    const activeUser = childUsers.find((u) => u.uid === active?.uid);
    if (activeUser) return activeUser;
    return childUsers[0];
  }, [selectedChildUid, childUsers, active]);

  useEffect(() => {
    if (!selectedChildUid && selectedUser) {
      dispatch(playerActions.setSelectedChildUid(selectedUser.uid));
    }
  }, [selectedChildUid, selectedUser, dispatch]);

  const { data: userConfig } = useUserConfig(selectedUser?.uid ?? "");
  const { mutate: updateUserConfig } = useUpdateUserConfig(
    selectedUser?.uid ?? "",
  );

  const { data: projects = [] } = useQuery<ProjectEntity[]>({
    queryKey: ["parent/getUserProjects", selectedUser?.uid],
    queryFn: () => getUserProjects(selectedUser.uid),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const { data: assetsData } = useQuery({
    queryKey: ["creator/getUserAssets", selectedUser?.uid],
    queryFn: () => getUserAssets({ uid: selectedUser.uid }),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });
  const assets = assetsData?.assets ?? [];

  const { data: feedbackHistory = [] } = useQuery<FeedbackHistoryItem[]>({
    queryKey: ["parent/getFeedbackHistory", selectedUser?.uid],
    queryFn: () => getFeedbackHistory({ uid: selectedUser.uid }),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const { data: status } = useQuery({
    queryKey: ["parent/getUserStatus", selectedUser?.uid],
    queryFn: () => getUserStatus(selectedUser!.uid),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const { data: aiChatSessionsData } = useQuery({
    queryKey: ["parent/getChildAIChatSessions", parent?.uid, selectedUser?.uid],
    queryFn: () =>
      getChildAIChatSessions({
        parentUid: parent!.uid,
        childUid: selectedUser!.uid,
      }),
    enabled: !!parent?.uid && !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });
  const aiChatSessions = aiChatSessionsData?.sessions ?? [];

  const { data: activitiesData } = useQuery({
    queryKey: ["parent/getUserActivities", parent?.uid, selectedUser?.uid],
    queryFn: () =>
      getUserActivities({
        parentUid: parent!.uid,
        childUid: selectedUser!.uid,
        limit: 50,
      }),
    enabled: !!parent?.uid && !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const activities = useMemo(() => {
    const allActivities = activitiesData?.activities ?? [];
    const now = dayjs();
    return allActivities.filter((activity) => {
      const activityTime = dayjs(activity.activity_at);
      return now.diff(activityTime, "hour") < 24;
    });
  }, [activitiesData]);

  const filteredProjects = useMemo(() => {
    if (!selectedUser?.uid) return [];
    return projects
      .filter(
        (p) =>
          !p.deleted_at &&
          p.type === ProjectType.BASIC &&
          p.author_uid === selectedUser.uid,
      )
      .sort(
        (a, b) => dayjs(b.created_time).unix() - dayjs(a.created_time).unix(),
      );
  }, [projects, selectedUser?.uid]);

  const handleSelectChild = (uid: string) => {
    dispatch(playerActions.setSelectedChildUid(uid));
  };

  const handleAddChild = () => {
    const kidsCount = users.filter(
      (u) => u.account_type !== AccountType.PARENTS,
    ).length;
    if (kidsCount >= (accountLimits?.players?.limit ?? 1)) {
      setAddChildLimitWarning(true);
    } else {
      setAddChildModalOpen(true);
    }
  };

  const handleDeleteChild = () => {
    if (!selectedUser) return;
    const activeKidsCount = users.filter(
      (u) =>
        u.account_type !== AccountType.PARENTS && u.type !== UserType.FROZEN,
    ).length;
    if (activeKidsCount <= 1) {
      setDeleteChildMinWarning(true);
    } else {
      setDeleteChildConfirm(true);
    }
  };

  const handleConfirmDeleteChild = () => {
    if (!selectedUser || !parent) return;
    setDeleteChildConfirm(false);
    dispatch(deleteChild({ uid: selectedUser.uid, parent_uid: parent.uid }))
      .unwrap()
      .then(() => {
        const chatRoomToRemove = chatRooms.find((room) =>
          room.members.some((m) => m.uid === selectedUser.uid),
        );
        if (chatRoomToRemove) {
          dispatch(messageActions.removeParentChatRoom(chatRoomToRemove.id));
        }
        dispatch(fetchAccountPlan({ uid: parent.uid }));
        const remainingUsers = childUsers.filter(
          (u) => u.uid !== selectedUser.uid,
        );
        if (remainingUsers.length > 0) {
          dispatch(playerActions.setSelectedChildUid(remainingUsers[0].uid));
        }
        scrollToTop?.();
      });
  };

  const handleOpenLanguageModal = () => {
    if (!userConfig || !selectedUser) return;
    setLanguageModalOpen(true);
  };

  const handleTogglePrivacyPublish = () => {
    if (!userConfig || !selectedUser) return;
    const newValue = !userConfig.project_publish;
    const newConfig = { ...userConfig, project_publish: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(
        actions.updateUserConfig({ key: "project_publish", value: newValue }),
      );
    }
    updateUserConfig(newConfig);
  };

  const handleTogglePrivacyCopy = () => {
    if (!userConfig || !selectedUser) return;
    const newValue = !userConfig.project_copied;
    const newConfig = { ...userConfig, project_copied: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(
        actions.updateUserConfig({ key: "project_copied", value: newValue }),
      );
    }
    updateUserConfig(newConfig);
  };

  const handleToggleAiChat = () => {
    if (!userConfig || !selectedUser) return;
    const newValue = !userConfig.ai_chat;
    const newConfig = { ...userConfig, ai_chat: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "ai_chat", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleToggleAiGeneration = () => {
    if (!userConfig || !selectedUser) return;
    const newValue = !userConfig.ai_generation;
    const newConfig = { ...userConfig, ai_generation: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(
        actions.updateUserConfig({ key: "ai_generation", value: newValue }),
      );
    }
    updateUserConfig(newConfig);
  };

  const handleOpenChangeUserStatus = () => {
    if (!selectedUser) return;
    if (
      selectedUser.uid === active?.uid &&
      selectedUser.type !== UserType.FROZEN
    ) {
      setChangeActiveWarning(true);
      return;
    }
    const kidsCount = users.filter(
      (u) => u.account_type !== AccountType.PARENTS,
    ).length;
    if (selectedUser.type === UserType.FROZEN) {
      if (kidsCount >= (accountLimits?.players?.limit ?? 1)) {
        setChangeMaxWarning(true);
      } else {
        setChange(true);
      }
    } else {
      if (kidsCount === 1) {
        setChangeMinWarning(true);
      } else {
        setChange(true);
      }
    }
  };

  const handleChangeUserStatus = () => {
    if (!selectedUser) return;
    btnDelay(async () => {
      const newType =
        selectedUser.type === UserType.FROZEN ? UserType.SUB : UserType.FROZEN;
      try {
        await dispatch(
          updateChildStatus({ uid: selectedUser.uid, type: newType }),
        ).unwrap();
      } catch (error) {
        console.error("Failed to update child status:", error);
      }
      setChange(false);
    });
  };

  const handleCloseDialog = () => {
    btnDelay(() => {
      setChange(false);
      setChangeMaxWarning(false);
      setChangeMinWarning(false);
      setChangeActiveWarning(false);
    });
  };

  const handleSwitchActiveUser = () => {
    if (!selectedUser || !parent) return;
    if (selectedUser.type === UserType.FROZEN) {
      setShowFrozenWarning(true);
      return;
    }
    setShowSwitchConfirm(true);
  };

  const handleConfirmSwitch = () => {
    if (!selectedUser || !parent) return;
    setShowSwitchConfirm(false);
    setSwitchLoading(true);
    handleFetchUserData({ parent_uid: parent.uid, uid: selectedUser.uid })
      .then(() => {
        dispatch(userActions.setActiveUser(selectedUser));
        setSwitchLoading(false);
      })
      .catch((error) => {
        console.error("Failed to switch active user:", error);
        setSwitchLoading(false);
      });
  };

  const handleToggleAssetTab = (tab: AssetTabType) => {
    setSelectedAssetTabs((prev) => {
      const newTabs = new Set(prev);
      if (newTabs.has(tab)) {
        newTabs.delete(tab);
      } else {
        newTabs.add(tab);
      }
      return newTabs;
    });
  };

  const handleDeleteAsset = (asset: Asset) => {
    if (!selectedUser) return;
    handleDialog({ type: DialogType.LOADING, value: true });
    deleteAsset({ id: asset.id })
      .then(() => {
        onAssetDeleted(selectedUser.uid, asset.id, asset.file_size || 0);
        setSelectedAsset(null);
        toast(
          <div>
            <p className="text text-textcolor-black">
              {i18n.t("MSG_TOAST_DELETED_SUCCESS")}
            </p>
          </div>,
        );
      })
      .catch((e) => {
        console.error(e);
        toast(
          <div>
            <p className="text text-textcolor-black">
              {i18n.t("MSG_TOAST_ERROR")}
            </p>
          </div>,
        );
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  return (
    <>
      {switchLoading && <Loading />}
      <ParentWarningModal
        open={showFrozenWarning}
        title={i18n.t("MSG_PARENT_PAGE_FROZEN_DIALOG_TITLE")}
        message={i18n.t("MSG_PARENT_PAGE_FROZEN_DIALOG_MESSAGE")}
        onClose={() => setShowFrozenWarning(false)}
      />
      <ParentConfirmModal
        open={showSwitchConfirm}
        icon="swap_horiz"
        title={i18n.t("MSG_PARENT_PAGE_SWITCH_CONFIRM_TITLE")}
        message={i18n.t("MSG_PARENT_PAGE_SWITCH_CONFIRM_MESSAGE", {
          name: selectedUser?.display_name,
        })}
        onClose={() => setShowSwitchConfirm(false)}
        onConfirm={handleConfirmSwitch}
      />
      <ParentConfirmModal
        open={change}
        icon="info"
        title={
          selectedUser?.type === UserType.FROZEN
            ? i18n.t("MSG_SETTING_PARENTS_ENABLE_KIDS_USER_TITLE")
            : i18n.t("MSG_SETTING_PARENTS_DISABLE_KIDS_USER_TITLE")
        }
        message={
          selectedUser?.type === UserType.FROZEN
            ? i18n.t("MSG_SETTING_PARENTS_ENABLE_KIDS_USER_MESSAGE", {
                name: selectedUser?.display_name,
              })
            : i18n.t("MSG_SETTING_PARENTS_DISABLE_KIDS_USER_MESSAGE", {
                name: selectedUser?.display_name,
              })
        }
        onClose={handleCloseDialog}
        onConfirm={handleChangeUserStatus}
      />
      <ParentWarningModal
        open={changeMaxWarning}
        title={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MAX_WARNING_TITLE")}
        message={i18n.t(
          "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MAX_WARNING_MESSAGE",
        )}
        onClose={handleCloseDialog}
      />
      <ParentWarningModal
        open={changeMinWarning}
        title={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MIN_WARNING_TITLE")}
        message={i18n.t(
          "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MIN_WARNING_MESSAGE",
        )}
        onClose={handleCloseDialog}
      />
      <ParentWarningModal
        open={changeActiveWarning}
        title={i18n.t(
          "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_ACTIVE_WARNING_TITLE",
        )}
        message={i18n.t(
          "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_ACTIVE_WARNING_MESSAGE",
        )}
        onClose={handleCloseDialog}
      />

      <div className="flex-col-el gap-6">
        {childUsers.length > 0 && (
          <ChildSelector
            childUsers={childUsers}
            selectedUser={selectedUser}
            activeUid={active?.uid}
            onSelectChild={handleSelectChild}
            onAddChild={handleAddChild}
          />
        )}
        {childUsers.length === 0 && (
          <div className="flex-col-el flex-center bg-white rounded-[16px] p-12 min-h-[400px]">
            <span className="material-symbols-outlined !text-[80px] text-gray2-60 mb-4">
              group
            </span>
            <h2 className="text-[28px] text-textcolor-black mb-2">
              {i18n.t("MSG_PARENT_PAGE_PLAYER_EMPTY_TITLE")}
            </h2>
            <p className="text-[21px] text-gray2-100 mb-6 text-center">
              {i18n.t("MSG_PARENT_PAGE_PLAYER_EMPTY_MESSAGE")}
            </p>
            <button
              onClick={handleAddChild}
              className="flex-row-el items-center gap-2 bg-blue-100 text-white rounded-[12px] px-6 py-3 text-[21px] border-none cursor-pointer hover:bg-blue-120 transition-all"
            >
              <span className="material-symbols-outlined text-xl">add</span>
              {i18n.t("MSG_PARENT_PAGE_PLAYER_ADD_BTN")}
            </button>
          </div>
        )}

        {selectedUser && (
          <div className="flex-col-el gap-6">
            <div className="bg-white rounded-[16px] p-6">
              <div className="flex-col-el items-center gap-3 mb-6">
                <div
                  className="relative cursor-pointer group"
                  onClick={() => btnDelay(() => setIconModalOpen(true))}
                >
                  <UserAvatar size={100} icon={selectedUser.icon} />
                  <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gray2-100 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <span className="material-symbols-outlined text-[27px] text-white">
                      photo_camera
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                  <div></div>
                  <span className="text-[30px] text-textcolor-black">
                    {selectedUser.display_name}
                  </span>
                  <div className="flex justify-start pl-2">
                    <button
                      onClick={() => btnDelay(() => setNameModalOpen(true))}
                      className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1.5 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
                    >
                      {i18n.t("MSG_PARENT_PAGE_CHANGE")}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center min-h-[40px]">
                  <div></div>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[18px] ${active?.uid === selectedUser.uid ? "bg-green-60/20 text-green-100" : "bg-gray2-20 text-gray2-100"}`}
                  >
                    <span className="material-symbols-outlined text-[21px]">
                      {active?.uid === selectedUser.uid
                        ? "check_circle"
                        : "pause_circle"}
                    </span>
                    {active?.uid === selectedUser.uid
                      ? i18n.t("MSG_PARENT_PAGE_ACTIVE")
                      : i18n.t("MSG_PARENT_PAGE_INACTIVE")}
                  </span>
                  <div className="flex justify-start pl-2">
                    <button
                      onClick={() => btnDelay(handleSwitchActiveUser)}
                      className={`bg-white border border-gray2-60 text-textcolor-black rounded-full py-1.5 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all ${active?.uid === selectedUser.uid ? "invisible" : ""}`}
                    >
                      {i18n.t("MSG_PARENT_PAGE_SWITCH_LOGIN")}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex-col-el">
                <div className="flex-col-el rounded-[12px] p-6 overflow-hidden">
                  <div className="flex-row-el items-center gap-2 text-[24px] text-textcolor-black mb-3 pb-3 border-b border-outline">
                    <span className="material-symbols-outlined !text-[45px] text-red-100">
                      person
                    </span>
                    {i18n.t("MSG_PARENT_PAGE_BASIC_INFO")}
                  </div>
                  <div className="flex-row-el items-center justify-between gap-2 mb-4">
                    <div className="flex-row-el gap-2">
                      {PARENT_PROFILE_TAB_OPTIONS.map((option) => {
                        const isActive = profileTab === option.key;
                        return (
                          <button
                            key={option.key}
                            className={`px-4 py-2 rounded-[12px] text-[18px] border-none transition-all cursor-pointer shrink-0 ${isActive ? "bg-ink text-white" : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"}`}
                            onClick={() => setProfileTab(option.key)}
                          >
                            {i18n.t(option.labelKey)}
                          </button>
                        );
                      })}
                    </div>
                    {profileTab === ParentProfileTab.APPEARANCE && (
                      <button
                        onClick={() =>
                          btnDelay(() => setAppearanceModalOpen(true))
                        }
                        className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1.5 px-4 text-[16px] cursor-pointer hover:bg-gray2-20 transition-all shrink-0"
                      >
                        {i18n.t("MSG_PARENT_PAGE_CHANGE")}
                      </button>
                    )}
                  </div>
                  {/* タブ切替でカードの高さが変わらないよう、全タブを同一セルに重ねて表示切替する */}
                  <div className="grid">
                    <div
                      className={`col-start-1 row-start-1 ${profileTab === ParentProfileTab.BASIC ? "" : "invisible"}`}
                    >
                      <BasicInfoSection
                        selectedUser={selectedUser}
                        onEditGender={() =>
                          btnDelay(() => setGenderModalOpen(true))
                        }
                        onEditBirthday={() => setBirthdayModalOpen(true)}
                      />
                    </div>
                    <div
                      className={`col-start-1 row-start-1 ${profileTab === ParentProfileTab.FAVORITES ? "" : "invisible"}`}
                    >
                      <FavoritesSection
                        selectedUser={selectedUser}
                        onEditField={(field) =>
                          btnDelay(() => setEditingField(field))
                        }
                      />
                    </div>
                    <div
                      className={`col-start-1 row-start-1 ${profileTab === ParentProfileTab.APPEARANCE ? "" : "invisible"}`}
                    >
                      <AppearanceSection selectedUser={selectedUser} />
                    </div>
                  </div>
                </div>
                <SettingsSection
                  selectedUser={selectedUser}
                  userConfig={userConfig}
                  onEditLanguage={handleOpenLanguageModal}
                  onTogglePublish={handleTogglePrivacyPublish}
                  onToggleCopy={handleTogglePrivacyCopy}
                  onToggleAiChat={handleToggleAiChat}
                  onToggleAiGeneration={handleToggleAiGeneration}
                  onChangeUserStatus={handleOpenChangeUserStatus}
                />
                <RecentActivitySection
                  activities={activities}
                  onShowMore={() => setShowActivitiesModal(true)}
                />
              </div>
            </div>
            <StatsCards status={status} />
            <ProjectsSection
              projects={filteredProjects}
              onSelectProject={(project) => setSelectedProject(project)}
              onShowMore={() => setProjectListOpen(true)}
            />
            <AssetsSection
              assets={assets}
              selectedAssetTabs={selectedAssetTabs}
              onSelectAsset={(asset) => setSelectedAsset(asset)}
              onShowMore={() => setAssetListOpen(true)}
              onToggleTab={handleToggleAssetTab}
              showSketchbook={true}
            />
            <FeedbackHistorySection
              feedbackItems={feedbackHistory}
              onShowMore={() => setFeedbackListOpen(true)}
              onSelectItem={(item) => setSelectedFeedback(item)}
            />
            <AIChatHistorySection
              sessions={aiChatSessions}
              onShowMore={() => setAIChatListOpen(true)}
              onSelectSession={(session) => setSelectedAIChatSession(session)}
            />
            {selectedUser.uid !== active?.uid && (
              <DangerZone onDelete={handleDeleteChild} />
            )}
          </div>
        )}

        {selectedUser && (
          <>
            <ChildNameEditModal
              open={nameModalOpen}
              currentName={selectedUser.display_name}
              uid={selectedUser.uid}
              onClose={() => setNameModalOpen(false)}
            />
            <GenderEditModal
              open={genderModalOpen}
              currentGender={selectedUser.gender}
              uid={selectedUser.uid}
              onClose={() => setGenderModalOpen(false)}
            />
            <BirthdayEditModal
              open={birthdayModalOpen}
              currentBirthday={selectedUser.birthday}
              uid={selectedUser.uid}
              onClose={() => setBirthdayModalOpen(false)}
            />
            <PlayerIconEditor
              open={iconModalOpen}
              onClose={() => setIconModalOpen(false)}
              targetUid={selectedUser.uid}
              currentIcon={selectedUser.icon}
              currentGender={selectedUser.gender}
              currentBirthday={selectedUser.birthday}
              currentFavoriteColor={selectedUser.favorite_color}
              currentBestSubject={selectedUser.best_subject}
              currentSelfIntroduction={selectedUser.self_introduction}
              currentAccessory={selectedUser.accessory}
              currentHairstyle={selectedUser.hairstyle}
              currentBodyType={selectedUser.body_type}
              currentFaceShape={selectedUser.face_shape}
              onSelect={(data) => {
                const userInfo: Record<string, unknown> = { icon: data.icon };
                if (data.gender !== undefined) userInfo.gender = data.gender;
                if (data.birthday !== undefined)
                  userInfo.birthday = data.birthday;
                if (data.favorite_color !== undefined)
                  userInfo.favorite_color = data.favorite_color;
                if (data.best_subject !== undefined)
                  userInfo.best_subject = data.best_subject;
                if (data.accessory !== undefined)
                  userInfo.accessory = data.accessory;
                if (data.hairstyle !== undefined)
                  userInfo.hairstyle = data.hairstyle;
                if (data.body_type !== undefined)
                  userInfo.body_type = data.body_type;
                if (data.face_shape !== undefined)
                  userInfo.face_shape = data.face_shape;
                dispatch(updateUser({ uid: selectedUser.uid, userInfo }));
              }}
            />
            {editingField && (
              <ProfileFieldEditModal
                open={!!editingField}
                uid={selectedUser.uid}
                fieldType={editingField}
                currentValue={
                  editingField === ProfileFieldType.SELF_INTRODUCTION
                    ? selectedUser.self_introduction
                    : editingField === ProfileFieldType.FAVORITE_COLOR
                      ? selectedUser.favorite_color
                      : editingField === ProfileFieldType.BEST_SUBJECT
                        ? selectedUser.best_subject
                        : editingField === ProfileFieldType.HAIRSTYLE
                          ? selectedUser.hairstyle
                          : editingField === ProfileFieldType.BODY_TYPE
                            ? selectedUser.body_type
                            : selectedUser.face_shape
                }
                onClose={() => setEditingField(null)}
              />
            )}
            <AppearanceEditModal
              open={appearanceModalOpen}
              uid={selectedUser.uid}
              currentAccessory={selectedUser.accessory}
              currentHairstyle={selectedUser.hairstyle}
              currentBodyType={selectedUser.body_type}
              currentFaceShape={selectedUser.face_shape}
              onClose={() => setAppearanceModalOpen(false)}
            />
          </>
        )}
        {selectedUser && userConfig && (
          <LanguageEditModal
            open={languageModalOpen}
            currentLocale={userConfig.locale}
            activeUid={active?.uid}
            selectedUid={selectedUser.uid}
            userConfig={userConfig}
            updateUserConfig={updateUserConfig}
            onClose={() => setLanguageModalOpen(false)}
          />
        )}
        <CreatePlayerModal
          open={addChildModalOpen}
          onClose={() => setAddChildModalOpen(false)}
          onSuccess={() => setAddChildModalOpen(false)}
        />
        <ChildUserLimitModal
          open={addChildLimitWarning}
          onClose={() => setAddChildLimitWarning(false)}
          limit={accountLimits?.players?.limit ?? 1}
        />
        <ParentConfirmModal
          open={deleteChildConfirm}
          icon="delete"
          title={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_TITLE")}
          message={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_MESSAGE", {
            name: selectedUser?.display_name,
          })}
          onClose={() => setDeleteChildConfirm(false)}
          onConfirm={handleConfirmDeleteChild}
        />
        <ParentWarningModal
          open={deleteChildMinWarning}
          title={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_MIN_TITLE")}
          message={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_MIN_MESSAGE")}
          onClose={() => setDeleteChildMinWarning(false)}
        />
        {selectedProject && (
          <ProjectPreviewModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
        {selectedAsset && (
          <AssetPreviewModal
            asset={selectedAsset}
            onClose={() => setSelectedAsset(null)}
            onDelete={handleDeleteAsset}
          />
        )}
        {projectListOpen && (
          <ProjectListModal
            projects={filteredProjects}
            onClose={() => setProjectListOpen(false)}
            onSelectProject={(project) => {
              setProjectListOpen(false);
              setSelectedProject(project);
            }}
          />
        )}
        {assetListOpen && (
          <AssetListModal
            assets={assets}
            initialTabs={selectedAssetTabs}
            onClose={() => setAssetListOpen(false)}
            onSelectAsset={(asset) => {
              setAssetListOpen(false);
              setSelectedAsset(asset);
            }}
            showSketchbook={true}
          />
        )}
        {selectedFeedback && (
          <FeedbackDetailModal
            item={selectedFeedback}
            childIcon={selectedUser?.icon}
            onClose={() => setSelectedFeedback(null)}
          />
        )}
        {feedbackListOpen && (
          <FeedbackListModal
            feedbackItems={feedbackHistory}
            onClose={() => setFeedbackListOpen(false)}
            onSelectItem={(item) => {
              setFeedbackListOpen(false);
              setSelectedFeedback(item);
            }}
          />
        )}
        {selectedAIChatSession && (
          <AIChatDetailModal
            session={selectedAIChatSession}
            childIcon={selectedUser?.icon}
            onClose={() => setSelectedAIChatSession(null)}
          />
        )}
        {aiChatListOpen && (
          <AIChatListModal
            sessions={aiChatSessions}
            onClose={() => setAIChatListOpen(false)}
            onSelectSession={(session) => {
              setAIChatListOpen(false);
              setSelectedAIChatSession(session);
            }}
          />
        )}
        {showActivitiesModal && (
          <ActivityListModal
            activities={activities}
            onClose={() => setShowActivitiesModal(false)}
          />
        )}
      </div>
    </>
  );
};
